import { useMemo, useState } from 'react';
import { Button, Card, CardHeader, Input, TextArea, Select, Checkbox } from 'components/ui';
import { MultiImageUploadField } from 'components/MultiImageUploadField';
import { humanizeEnum } from 'lib/formatters';
import { useCatalogEnums } from '../hooks/useCatalogEnums';
import { buildCatalogInput, validateCatalogForm } from '../utils/buildCatalogInput';
import { HsnCodeSelect } from './HsnCodeSelect';

const INITIAL_FORM = {
  name: '',
  brand: '',
  shortDescription: '',
  description: '',
  category: '',
  subcategory: '',
  hsnCode: '',
  unit: '',
  unitSize: '',
  weight: '',
  images: [],
  vendorPrice: '',
  mrp: '',
  isGstIncluded: false,
  availableQuantity: '',
  minimumOrderQuantity: '1',
  lowStockThreshold: '',
  estimatedPrepTime: '',
  isVegetarian: false,
  isVegan: false,
  isGlutenFree: false,
  isDairyFree: false,
  isNutFree: false,
  isHalal: false,
  isKeto: false,
  isOrganic: false,
};

const DIETARY_FLAGS = [
  { key: 'isVegetarian', label: 'Vegetarian' },
  { key: 'isVegan', label: 'Vegan' },
  { key: 'isGlutenFree', label: 'Gluten-free' },
  { key: 'isDairyFree', label: 'Dairy-free' },
  { key: 'isNutFree', label: 'Nut-free' },
  { key: 'isHalal', label: 'Halal' },
  { key: 'isKeto', label: 'Keto' },
  { key: 'isOrganic', label: 'Organic' },
];

/**
 * Reusable product form for both "Add" and "Edit". Pass `initialValues` to
 * prefill (edit) and `submitLabel` for the primary button text.
 *
 * @param {(input: object) => Promise<object|null>} onSubmit resolves to the
 *   mutation payload (or null on network error)
 * @param {boolean} submitting
 * @param {(payload: object) => void} onSuccess called when payload.success
 * @param {() => void} [onCancel]
 * @param {object} [initialValues] partial form values to prefill
 * @param {string} [submitLabel='Add product']
 */
export function ProductForm({
  onSubmit,
  submitting,
  onSuccess,
  onCancel,
  initialValues,
  submitLabel = 'Add product',
}) {
  const [form, setForm] = useState(() => ({ ...INITIAL_FORM, ...(initialValues || {}) }));
  const [errors, setErrors] = useState({});

  const {
    categoryOptions,
    unitOptions,
    getSubcategoryOptions,
    loading: enumsLoading,
  } = useCatalogEnums();

  const subcategoryOptions = useMemo(() => {
    const options = getSubcategoryOptions(form.category);
    // Ensure a prefilled subcategory stays selectable even if it isn't in the
    // category-filtered list yet (enums still loading, or category mismatch).
    if (form.subcategory && !options.some((option) => option.value === form.subcategory)) {
      return [{ value: form.subcategory, label: humanizeEnum(form.subcategory) }, ...options];
    }
    return options;
  }, [getSubcategoryOptions, form.category, form.subcategory]);

  const setField = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'category') {
        next.subcategory = ''; // reset dependent select
        next.hsnCode = '';
      }
      if (field === 'subcategory') next.hsnCode = ''; // re-suggest HSN for the new subcategory
      return next;
    });
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const validationErrors = validateCatalogForm(form);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const payload = await onSubmit(buildCatalogInput(form));
    if (!payload) return; // network error already surfaced by the caller

    if (payload.success) {
      onSuccess?.(payload);
      return;
    }

    // Map server-side field errors back onto the form.
    const serverErrors = {};
    (payload.errors || []).forEach((err) => {
      if (err.field) serverErrors[err.field] = err.message;
    });
    setErrors((prev) => ({ ...prev, ...serverErrors }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <Card>
        <CardHeader title="Product images" subtitle="The first image is the primary photo." />
        <MultiImageUploadField
          primary
          values={form.images}
          onChange={(images) => setField('images', images)}
          hint="Upload one or more photos. Hover a photo and tap “Make primary” to reorder."
        />
      </Card>

      <Card>
        <CardHeader title="Product details" subtitle="Basic information about the product." />
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Product name"
            required
            containerClassName="sm:col-span-2"
            placeholder="e.g. Organic Almonds 500g"
            value={form.name}
            onChange={(event) => setField('name', event.target.value)}
            error={errors.name}
          />
          <Input
            label="Brand"
            placeholder="Optional"
            value={form.brand}
            onChange={(event) => setField('brand', event.target.value)}
          />
          <Select
            label="Unit"
            placeholder="Select unit"
            options={unitOptions}
            disabled={enumsLoading}
            value={form.unit}
            onChange={(event) => setField('unit', event.target.value)}
          />
          <Input
            label="Unit size"
            placeholder="e.g. 500 g"
            hint="Net size shown to customers"
            value={form.unitSize}
            onChange={(event) => setField('unitSize', event.target.value)}
          />
          <Input
            label="Weight"
            type="number"
            min="0"
            step="any"
            placeholder="Shipping weight"
            value={form.weight}
            onChange={(event) => setField('weight', event.target.value)}
            error={errors.weight}
          />
          <Input
            label="Short description"
            containerClassName="sm:col-span-2"
            placeholder="One-line summary"
            value={form.shortDescription}
            onChange={(event) => setField('shortDescription', event.target.value)}
          />
          <TextArea
            label="Description"
            containerClassName="sm:col-span-2"
            placeholder="Describe the product for your customers"
            value={form.description}
            onChange={(event) => setField('description', event.target.value)}
          />
        </div>
      </Card>

      <Card>
        <CardHeader title="Classification" subtitle="Helps customers find your product." />
        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Category"
            required
            placeholder="Select category"
            options={categoryOptions}
            disabled={enumsLoading}
            value={form.category}
            onChange={(event) => setField('category', event.target.value)}
            error={errors.category}
          />
          <Select
            label="Subcategory"
            required
            placeholder={form.category ? 'Select subcategory' : 'Select a category first'}
            options={subcategoryOptions}
            disabled={enumsLoading || !form.category}
            value={form.subcategory}
            onChange={(event) => setField('subcategory', event.target.value)}
            error={errors.subcategory}
          />
          <HsnCodeSelect
            subcategory={form.subcategory}
            value={form.hsnCode}
            onChange={(code) => setField('hsnCode', code)}
          />
        </div>
      </Card>

      <Card>
        <CardHeader title="Pricing & inventory" />
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Your selling price"
            required
            type="number"
            min="0"
            step="any"
            leftAddon="₹"
            value={form.vendorPrice}
            onChange={(event) => setField('vendorPrice', event.target.value)}
            error={errors.vendorPrice}
          />
          <Input
            label="MRP"
            type="number"
            min="0"
            step="any"
            leftAddon="₹"
            hint="Maximum retail price"
            value={form.mrp}
            onChange={(event) => setField('mrp', event.target.value)}
            error={errors.mrp}
          />
          <Checkbox
            label="Selling price includes GST"
            className="sm:col-span-2"
            checked={form.isGstIncluded}
            onChange={(event) => setField('isGstIncluded', event.target.checked)}
          />
          <Input
            label="Stock quantity"
            required
            type="number"
            min="0"
            step="any"
            value={form.availableQuantity}
            onChange={(event) => setField('availableQuantity', event.target.value)}
            error={errors.availableQuantity}
          />
          <Input
            label="Low stock alert at"
            type="number"
            min="0"
            step="any"
            hint="Warn when stock falls to this level"
            value={form.lowStockThreshold}
            onChange={(event) => setField('lowStockThreshold', event.target.value)}
            error={errors.lowStockThreshold}
          />
          <Input
            label="Minimum order quantity"
            required
            type="number"
            min="0"
            step="any"
            value={form.minimumOrderQuantity}
            onChange={(event) => setField('minimumOrderQuantity', event.target.value)}
            error={errors.minimumOrderQuantity}
          />
          <Input
            label="Prep time (minutes)"
            type="number"
            min="0"
            step="1"
            hint="For made-to-order items"
            value={form.estimatedPrepTime}
            onChange={(event) => setField('estimatedPrepTime', event.target.value)}
            error={errors.estimatedPrepTime}
          />
        </div>
      </Card>

      <Card>
        <CardHeader title="Dietary attributes" subtitle="Optional — applies to food items." />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {DIETARY_FLAGS.map(({ key, label }) => (
            <Checkbox
              key={key}
              label={label}
              checked={form[key]}
              onChange={(event) => setField(key, event.target.checked)}
            />
          ))}
        </div>
      </Card>

      <div className="flex items-center justify-end gap-3">
        {onCancel && (
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button type="submit" isLoading={submitting}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}

export default ProductForm;
